import React, { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";
import formatDate from "date-fns/format";
import DayPicker, { DateUtils } from "react-day-picker";
import { Popover, PopoverBody } from "reactstrap";
import "react-day-picker/lib/style.css";
import "./date-picker.scss";

const DEFAULT_DISPLAY_FORMAT = "M/d/yy";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseLocalDate = value => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value);
  if (!dateOnlyMatch) return new Date(value);

  const [, year, month, day] = dateOnlyMatch;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const formatDateForInput = date => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (date, format) => (date ? formatDate(date, format) : "");

const startOfDay = date => {
  if (!date) return null;
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const endOfDay = date => {
  if (!date) return null;
  const result = new Date(date);
  result.setHours(23, 59, 0, 0);
  return result;
};

const propTypes = {
  isRange: PropTypes.bool,
  requireApply: PropTypes.bool,
  from: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  isOpen: PropTypes.bool,
  disabledDays: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  label: PropTypes.string,
  nameFrom: PropTypes.string,
  nameTo: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  placement: PropTypes.string,
  showValueInTrigger: PropTypes.bool,
  popoverHeading: PropTypes.string,
  onClose: PropTypes.func,
  format: PropTypes.string
};

const DatePicker = ({
  isRange = false,
  requireApply = true,
  from = null,
  to = null,
  onChange = () => {},
  onClick = () => {},
  isOpen = undefined,
  disabledDays: disabledDaysProp = undefined,
  minDate = null,
  maxDate = null,
  label = "",
  nameFrom = undefined,
  nameTo = undefined,
  className = "",
  disabled = false,
  showValueInTrigger = true,
  placement = "bottom",
  format = DEFAULT_DISPLAY_FORMAT,
  popoverHeading = null,
  onClose = null
}) => {
  const pickerId = useId();
  const triggerId = `date-picker-${pickerId}`;
  const usesInternalSelection = isRange && requireApply;

  const [pendingFrom, setPendingFrom] = useState(() => parseLocalDate(from));
  const [pendingTo, setPendingTo] = useState(() => parseLocalDate(to));
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    setPendingFrom(parseLocalDate(from));
    setPendingTo(parseLocalDate(to));
  }, [from, to]);

  const isControlledOpen = isOpen !== undefined;
  const open = isControlledOpen ? isOpen : internalIsOpen;

  const propFromDate = parseLocalDate(from);
  const propToDate = parseLocalDate(to);

  const selectedFrom = usesInternalSelection ? pendingFrom : propFromDate;
  const selectedTo = usesInternalSelection ? pendingTo : propToDate;

  const closePopover = () => {
    if (!isControlledOpen) setInternalIsOpen(false);
  };

  const togglePopover = () => {
    if (isControlledOpen) {
      if (onClose) onClose();
      return;
    }
    setInternalIsOpen(!internalIsOpen);
  };

  const handleClose = () => {
    if (onClose) onClose();
    closePopover();
  };

  const handleDayClick = (day, modifiers = {}) => {
    if (modifiers.disabled) return;

    if (!isRange) {
      onChange(day, undefined);
      return;
    }

    const range = DateUtils.addDayToRange(day, { from: selectedFrom, to: selectedTo });

    if (requireApply) {
      setPendingFrom(range.from);
      setPendingTo(range.to);
    } else {
      onChange(startOfDay(range.from), endOfDay(range.to));
    }
  };

  const handleApply = () => {
    onChange(startOfDay(pendingFrom), endOfDay(pendingTo));
    closePopover();
  };

  const handleCancel = () => {
    setPendingFrom(null);
    setPendingTo(null);
    closePopover();
  };

  const navigateMonth = direction => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const disabledDays = {
    ...(minDate ? { before: parseLocalDate(minDate) } : {}),
    ...(maxDate ? { after: parseLocalDate(maxDate) } : {}),
    ...disabledDaysProp
  };

  const getDisplayText = () => {
    if (!showValueInTrigger) return label;

    if (isRange) {
      if (selectedFrom && selectedTo) {
        return `${formatDateForDisplay(selectedFrom, format)} - ${formatDateForDisplay(
          selectedTo,
          format
        )}`;
      }
      return selectedFrom ? `From ${formatDateForDisplay(selectedFrom, format)}` : label;
    }
    return selectedFrom ? formatDateForDisplay(selectedFrom, format) : label;
  };

  return (
    <>
      <button
        type="button"
        id={triggerId}
        className={`date-picker__trigger ${className}`.trim()}
        onClick={onClick}
        disabled={disabled}
      >
        {getDisplayText()}
      </button>

      {nameFrom && nameTo && (
        <>
          <input type="hidden" name={nameFrom} value={formatDateForInput(selectedFrom)} />
          <input type="hidden" name={nameTo} value={formatDateForInput(selectedTo)} />
        </>
      )}

      <Popover
        placement={placement}
        isOpen={open}
        target={triggerId}
        toggle={togglePopover}
        trigger={isRange && requireApply ? "click" : "legacy"}
        className="date-picker__popover"
      >
        <PopoverBody className="date-picker__popover-body">
          {popoverHeading && (
            <div className="date-picker__popover-heading">
              <span className="date-picker__popover-heading-title">{popoverHeading}</span>
              {onClose && (
                <button
                  type="button"
                  className="date-picker__popover-heading-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  ×
                </button>
              )}
            </div>
          )}
          <div className="date-picker__header">
            <button
              type="button"
              className="date-picker__nav-button"
              onClick={() => navigateMonth(-1)}
            >
              ‹
            </button>
            <div className="date-picker__month-year">{formatDate(currentMonth, "MMMM yyyy")}</div>
            <button
              type="button"
              className="date-picker__nav-button"
              onClick={() => navigateMonth(1)}
            >
              ›
            </button>
          </div>

          <DayPicker
            className="date-picker__calendar"
            month={currentMonth}
            selectedDays={
              isRange ? [selectedFrom, { from: selectedFrom, to: selectedTo }] : [selectedFrom]
            }
            modifiers={{ start: selectedFrom, end: isRange ? selectedTo : selectedFrom }}
            onDayClick={handleDayClick}
            disabledDays={disabledDays}
            showOutsideDays={false}
            onMonthChange={setCurrentMonth}
            captionElement={() => null}
            navbarElement={() => null}
          />

          {isRange && requireApply && (
            <div className="date-picker__actions">
              <button
                type="button"
                className="date-picker__button date-picker__button--cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="date-picker__button date-picker__button--apply"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          )}
        </PopoverBody>
      </Popover>
    </>
  );
};

DatePicker.propTypes = propTypes;

export default DatePicker;
